import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const isOwnApiRequest = (config) => {
  try {
    const api = new URL(`${API_URL}/api/`, window.location.origin);
    const request = new URL(config?.url, config?.baseURL || window.location.origin);
    return request.origin === api.origin && request.pathname.startsWith(api.pathname);
  } catch {
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(token);
  const sessionVersion = useRef(0);
  const mounted = useRef(true);

  const logout = useCallback(() => {
    sessionVersion.current += 1;
    tokenRef.current = null;
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    mounted.current = true;
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        // A disabled/expired account must lose access on any authenticated API
        // request. Never let an old token's delayed 401 clear a newer session.
        const headers = error.config?.headers;
        const authorization = headers?.get?.('Authorization') || headers?.Authorization || headers?.authorization;
        if (mounted.current && error.response?.status === 401 && tokenRef.current &&
            authorization === `Bearer ${tokenRef.current}` && isOwnApiRequest(error.config)) {
          logout();
        }
        return Promise.reject(error);
      }
    );
    return () => {
      mounted.current = false;
      sessionVersion.current += 1;
      axios.interceptors.response.eject(interceptor);
    };
  }, [logout]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    const version = sessionVersion.current;
    const controller = new AbortController();
    const isCurrent = () => mounted.current && !controller.signal.aborted &&
      tokenRef.current === token && sessionVersion.current === version;
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setLoading(true);
    axios.get(`${API_URL}/api/auth/me`, {
      signal: controller.signal,
      headers: { Authorization: `Bearer ${token}` }
    }).then(response => {
      if (isCurrent()) setUser(response.data);
    }).catch(() => {
      if (isCurrent()) logout();
    }).finally(() => {
      if (isCurrent()) setLoading(false);
    });
    return () => controller.abort();
  }, [token, logout]);

  const authenticate = async (path, data) => {
    const version = ++sessionVersion.current;
    const response = await axios.post(`${API_URL}/api/auth/${path}`, data);
    if (!mounted.current || version !== sessionVersion.current) {
      throw new Error('Permintaan masuk dibatalkan. Silakan masuk kembali.');
    }
    const { token: newToken, user: userData } = response.data;
    tokenRef.current = newToken;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    setLoading(false);
    return userData;
  };

  const login = (email, password) => authenticate('login', { email, password });
  // Public registration can never be used to choose a privileged role.
  const register = data => authenticate('register', { ...data, role: 'penghuni' });

  const isPemilik = () => user?.role === 'pemilik';
  const isAdmin = () => user?.role === 'admin';
  const isPenghuni = () => user?.role === 'penghuni';
  const canManageKos = () => ['pemilik', 'admin'].includes(user?.role);

  const refreshUser = async () => {
    const currentToken = tokenRef.current;
    const version = sessionVersion.current;
    if (!currentToken) return;
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      if (mounted.current && tokenRef.current === currentToken && version === sessionVersion.current) {
        setUser(response.data);
      }
    } catch {
      // The response interceptor handles 401; transient errors keep the current
      // profile, without logging an Axios config containing credentials.
    }
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, register, logout,
      isPemilik, isAdmin, isPenghuni, canManageKos,
      isAuthenticated: !!user,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
